import type { ReactiveSet } from '@solid-primitives/set'
import { Title } from '@solidjs/meta'
import { type Component, createEffect, createMemo, createResource, createSignal, Show } from 'solid-js'
import { createStore } from 'solid-js/store'
import { z } from 'zod'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Card from '~/components/Card'
import Checkbox from '~/components/Checkbox'
import FabMenu from '~/components/FabMenu'
import Form from '~/components/Form'
import Icon from '~/components/Icon'
import InfiniteList from '~/components/InfiniteList'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Select from '~/components/Select'
import SwipeAction from '~/components/SwipeAction'
import Switch from '~/components/Switch'
import Table from '~/components/Table'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useCompanySettings } from '~/hooks/useCompanySettings'
import { useDebouncedSignal } from '~/hooks/useDebouncedSignal'
import { useForm } from '~/hooks/useForm'
import { useIsMobile } from '~/hooks/useIsMobile'
import { usePermissions } from '~/hooks/usePermissions'
import { useServerTable } from '~/hooks/useServerTable'
import TableFilters, { FiltersButton } from '~/layers/TableFilters'
import { type Path, useNavigate } from '~/router'
import { type CustomerStatsType, type CustomerType, customerService } from '~/services/customer.service'
import { P } from '~/utils/permissions'
import { customerStateColors, customerStateLabels, documentTypeOptions } from '~/utils/settings'

const STAT_CARDS: Array<{
  key: keyof CustomerStatsType
  label: string
  icon: string
  color: string
}> = [
  { key: 'total', label: 'Total clientes', icon: 'material-symbols-light:group-outline', color: 'text-primary' },
  { key: 'nuevosMes', label: 'Nuevos del mes', icon: 'material-symbols-light:person-add-outline', color: 'text-info' },
  { key: 'activos', label: 'Activos', icon: 'material-symbols-light:verified-user-outline', color: 'text-success' },
  { key: 'inactivos', label: 'Inactivos', icon: 'material-symbols-light:person-off-outline', color: 'text-foreground-subtle' },
  { key: 'conDeuda', label: 'Con deuda', icon: 'material-symbols-light:account-balance-wallet-outline', color: 'text-warning' },
  { key: 'morosos', label: 'Morosos', icon: 'material-symbols-light:error-outline', color: 'text-danger' }
]

const CustomersPage: Component = () => {
  const { can } = usePermissions()
  const alert = useAlert()
  const toast = useToast()
  const navigate = useNavigate()
  const { settings } = useCompanySettings()
  const isMobile = useIsMobile()

  /** Colores y etiquetas de estado vienen de `companies.settings.customerStates`. */
  const estadoColor = () => customerStateColors(settings()?.customerStates ?? [])
  const estadoLabel = () => customerStateLabels(settings()?.customerStates ?? [])
  const documentOptions = () => documentTypeOptions(settings()?.documentTypes ?? [])

  // Búsqueda y filtros server-side
  const [filtersOpen, setFiltersOpen] = createSignal(false)
  const [filterEstado, setFilterEstado] = createSignal('')
  const [filterTipo, setFilterTipo] = createSignal('')
  const [filterCiudad, setFilterCiudad] = createSignal('')
  const [filterBloqueado, setFilterBloqueado] = createSignal(false)
  const [filterBusqueda, debouncedSearch, setFilterBusqueda] = useDebouncedSignal('')

  const buildParams = (): Record<string, string | number | undefined> => {
    const q = debouncedSearch().trim()
    const p: Record<string, string | number | undefined> = {}
    if (q) p.name = `%${q}%`
    if (filterEstado()) p.estadoCliente = filterEstado()
    if (filterTipo()) p.tipoPersona = filterTipo()
    if (filterCiudad()) p.ciudad = filterCiudad()
    return p
  }

  const table = useServerTable<CustomerType>({
    fetcher: params => customerService.list(params as Parameters<typeof customerService.list>[0]),
    params: buildParams
  })

  // ── Scroll infinito móvil ────────────────────────────────────────────────
  const [mobileItems, setMobileItems] = createStore<CustomerType[]>([])
  const [mobileLoading, setMobileLoading] = createSignal(false)
  const [mobileTotal, setMobileTotal] = createSignal(0)
  const hasMore = () => mobileItems.length < mobileTotal()

  const mobileFetch = async (reset: boolean) => {
    if (!reset && mobileLoading()) return
    setMobileLoading(true)
    try {
      const page = reset ? 1 : Math.floor(mobileItems.length / table.perPage()) + 1
      const res = await customerService.list({ ...buildParams(), page, limit: table.perPage() })
      setMobileTotal(res.meta.total)
      setMobileItems(reset ? res.items : prev => [...prev, ...res.items])
    } catch {
      // error silencioso: el sentinela reintentará en el siguiente scroll
    } finally {
      setMobileLoading(false)
    }
  }

  // Reinicia la lista (y recarga desde la página 1) cuando cambian los filtros.
  // Al entrar en móvil la clave pasa de '' a los filtros actuales, así que este
  // mismo efecto hace la primera carga sin duplicar peticiones.
  const [filtersKey, setFiltersKey] = createSignal('')
  createEffect(() => {
    if (!isMobile()) return
    const key = JSON.stringify([filterEstado(), filterTipo(), filterCiudad(), debouncedSearch()])
    if (key === filtersKey()) return
    setFiltersKey(key)
    void mobileFetch(true)
  })

  const loadMore = () => {
    if (!hasMore() || mobileLoading()) return
    void mobileFetch(false)
  }

  const customers = createMemo(() => {
    const list = table.data()
    if (!filterBloqueado()) return list
    return list.filter(c => c.bloqueado)
  })
  const [stats, { refetch: refetchStats }] = createResource(async () => {
    try {
      return await customerService.stats()
    } catch {
      return null
    }
  })

  const activeFilters = createMemo(() => [filterEstado(), filterTipo(), filterCiudad()].filter(v => v !== '').length + (filterBloqueado() ? 1 : 0) + (filterBusqueda() ? 1 : 0))

  const clearFilters = () => {
    setFilterEstado('')
    setFilterTipo('')
    setFilterCiudad('')
    setFilterBloqueado(false)
    setFilterBusqueda('')
  }

  const customerChips = createMemo(() => [
    ...(filterBusqueda() ? [{ label: `«${filterBusqueda()}»`, color: 'info' as const, icon: 'material-symbols-light:search', onRemove: () => setFilterBusqueda('') }] : []),
    ...(filterEstado() ? [{ label: estadoLabel()[filterEstado()] ?? filterEstado(), color: 'primary' as const, onRemove: () => setFilterEstado('') }] : []),
    ...(filterTipo() ? [{ label: filterTipo() === 'juridica' ? 'Persona jurídica' : 'Persona natural', color: 'success' as const, onRemove: () => setFilterTipo('') }] : []),
    ...(filterCiudad() ? [{ label: filterCiudad(), color: 'warning' as const, onRemove: () => setFilterCiudad('') }] : []),
    ...(filterBloqueado() ? [{ label: 'Bloqueados', color: 'danger' as const, onRemove: () => setFilterBloqueado(false) }] : [])
  ])

  const [showModal, setShowModal] = createSignal(false)
  const [editingCustomer, setEditingCustomer] = createSignal<CustomerType | null>(null)

  // Los campos opcionales viajan como cadena vacía desde el DOM; el esquema los
  // convierte a undefined para no guardar vacíos donde la API espera ausencia.
  const optionalText = z
    .string()
    .trim()
    .optional()
    .transform(value => value || undefined)

  const customerSchema = z.object({
    name: z.string().trim().min(1, 'El nombre o la razón social es obligatorio'),
    document: optionalText,
    documentType: z.string().trim().default('CC'),
    phone: optionalText,
    celular: optionalText,
    email: z.union([z.literal(''), z.string().email('Correo electrónico inválido')]).transform(value => value || undefined),
    address: optionalText,
    ciudad: optionalText
  })

  const form = useForm({
    schema: customerSchema,
    initialValues: () => ({
      name: editingCustomer()?.name ?? '',
      document: editingCustomer()?.document ?? '',
      documentType: editingCustomer()?.documentType ?? 'CC',
      phone: editingCustomer()?.phone ?? '',
      celular: editingCustomer()?.celular ?? '',
      email: editingCustomer()?.email ?? '',
      address: editingCustomer()?.address ?? '',
      ciudad: editingCustomer()?.ciudad ?? ''
    }),
    onSubmit: async values => {
      const payload = { ...values, estadoCliente: 'activo' as const }

      if (editingCustomer()) {
        await customerService.update(editingCustomer()!.id, payload)
        toast.addToast({ title: 'Cliente actualizado', type: 'success' })
      } else {
        await customerService.create(payload)
        toast.addToast({ title: 'Cliente creado', type: 'success' })
      }
      setShowModal(false)
      await table.refetch()
      await refetchStats()
    },
    onError: err => toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
  })

  const openCreateModal = () => {
    setEditingCustomer(null)
    form.reset()
    setShowModal(true)
  }

  const openEditModal = (customer: CustomerType) => {
    setEditingCustomer(customer)
    setShowModal(true)
  }

  const handleDelete = async (id: number) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar cliente',
      message: '¿Estás seguro de eliminar este cliente?'
    })
    if (!ok) return
    try {
      await customerService.delete(id)
      toast.addToast({ title: 'Cliente eliminado', type: 'success' })
      setMobileItems(prev => prev.filter(i => i.id !== id))
      await table.refetch()
      await refetchStats()
    } catch (err: unknown) {
      toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
    }
  }

  // ── Selección masiva ────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = createSignal<ReadonlySet<number>>(new Set())

  const handleSelectionChange = (selected: ReactiveSet<unknown>) => {
    const next = new Set<number>()
    for (const value of selected.values()) next.add(value as number)
    setSelectedIds(next)
  }

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleBulkDelete = async () => {
    const ids = [...selectedIds()]
    if (ids.length === 0) return
    const ok = await alert.confirmDelete({
      title: 'Eliminar clientes',
      message: `¿Eliminar ${ids.length} ${ids.length === 1 ? 'cliente' : 'clientes'}? Esta acción no se puede deshacer.`
    })
    if (!ok) return
    try {
      const res = await customerService.bulkDelete(ids)
      toast.addToast({ title: `${res.deleted} ${res.deleted === 1 ? 'cliente eliminado' : 'clientes eliminados'}`, type: 'success' })
      setSelectedIds(new Set<number>())
      setMobileItems(prev => prev.filter(i => !ids.includes(i.id)))
      await table.refetch()
      await refetchStats()
    } catch (err: unknown) {
      toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
    }
  }

  const columns = [
    {
      header: 'Cliente',
      accessor: (c: CustomerType) => (
        <div class="flex items-center gap-3">
          <div class="flex flex-col">
            <span class="font-medium text-foreground">{c.tipoPersona === 'juridica' ? c.razonSocial || c.name : [c.name, c.lastName].filter(Boolean).join(' ')}</span>
            <span class="text-xs text-foreground-subtle">{c.nombreComercial || c.email || ''}</span>
          </div>
        </div>
      )
    },
    {
      header: 'Documento',
      accessor: (c: CustomerType) => (
        <div class="flex flex-col">
          <span class="text-foreground">{c.document || '—'}</span>
          <span class="text-xs text-foreground-subtle">{c.documentType || ''}</span>
        </div>
      )
    },
    { header: 'Teléfono', accessor: (c: CustomerType) => c.phone || c.celular || '-' },
    { header: 'Correo', accessor: (c: CustomerType) => c.email || '-' },
    { header: 'Ciudad', accessor: (c: CustomerType) => c.ciudad || '-' },
    {
      header: 'Estado',
      accessor: (c: CustomerType) => (
        <div class="flex flex-col gap-1">
          <Badge color={estadoColor()[c.estadoCliente]}>{estadoLabel()[c.estadoCliente] ?? c.estadoCliente}</Badge>
          {c.bloqueado ? <Badge color="danger">Bloqueado</Badge> : null}
        </div>
      )
    }
  ]

  return (
    <>
      <Title>Clientes | Florez Jaramillo</Title>

      {/* Dashboard de métricas del módulo. En móvil van en una fila con scroll
          horizontal (6 cards no caben en 2 columnas sin apretujarse); en lg+ se
          reparten en grid. */}
      <Show when={stats()}>
        {s => (
          <div class="flex gap-4 overflow-x-auto hide-scrollbar mb-6 lg:grid lg:grid-cols-3 lg:overflow-visible xl:grid-cols-6">
            {STAT_CARDS.map(stat => (
              <Card class="min-w-40 lg:min-w-0">
                <Card.Body>
                  <div class="flex items-center justify-between gap-2">
                    <div>
                      <p class="text-2xl font-bold text-foreground">{s()[stat.key] ?? 0}</p>
                      <p class="text-xs text-foreground-subtle">{stat.label}</p>
                    </div>
                    <Icon icon={stat.icon} size={24} class={stat.color} />
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        )}
      </Show>

      <Show when={!isMobile()}>
        <Table<CustomerType>
          title="Clientes"
          subtitle="Información maestra, tributaria y comercial de tus clientes"
          headerActions={
            <div class="hidden sm:flex items-center gap-2">
              <FiltersButton activeCount={activeFilters()} onClick={() => setFiltersOpen(true)} />
              <Show when={can(P.customers.create)}>
                <Button onClick={openCreateModal} prependIcon="material-symbols-light:add-circle-outline">
                  Nuevo Cliente
                </Button>
              </Show>
            </div>
          }
          filterChips={customerChips()}
          data={customers()}
          columns={columns}
          keyField="id"
          headSticky
          loading={table.loading()}
          serverSide
          selectable={can(P.customers.delete)}
          selectableColor="danger"
          onSelectionChange={handleSelectionChange}
          onDeleteSelected={() => void handleBulkDelete()}
          total={table.total()}
          page={table.page()}
          dense
          perPage={table.perPage()}
          onPageChange={table.onPageChange}
          onPerPageChange={table.onPerPageChange}
          emptyMessage="Aún no has registrado clientes"
          onRowClick={c => navigate(`/admin/clientes/${c.id}` as Path)}
          actions={customer => (
            <ButtonGroup size="sm" variant="soft">
              <Button
                color="info"
                onClick={e => {
                  e.stopPropagation()
                  navigate(`/admin/clientes/${customer.id}` as Path)
                }}
                tooltip="Ver detalle"
                icon="material-symbols-light:visibility-outline"
              />
              <Show when={can(P.customers.update)}>
                <Button
                  color="warning"
                  onClick={e => {
                    e.stopPropagation()
                    openEditModal(customer)
                  }}
                  tooltip="Editar"
                  icon="material-symbols-light:edit-outline"
                />
              </Show>
              <Show when={can(P.customers.delete)}>
                <Button
                  color="danger"
                  onClick={e => {
                    e.stopPropagation()
                    handleDelete(customer.id)
                  }}
                  tooltip="Eliminar"
                  icon="material-symbols-light:delete-outline"
                />
              </Show>
            </ButtonGroup>
          )}
        />
      </Show>

      {/* Vista móvil: en pantallas pequeñas la tabla desborda, así que se
          sustituye por tarjetas con acciones deslizables y scroll infinito. */}
      <Show when={isMobile()}>
        <Show when={can(P.customers.delete) && selectedIds().size > 0}>
          <div class="mb-3 flex items-center justify-between gap-2 rounded-xl border border-danger-soft bg-danger-soft/30 px-4 py-3">
            <span class="text-sm font-semibold text-danger">
              {selectedIds().size} {selectedIds().size === 1 ? 'seleccionado' : 'seleccionados'}
            </span>
            <Button size="sm" color="danger" variant="soft" icon="material-symbols-light:delete-outline" onClick={() => void handleBulkDelete()}>
              Eliminar
            </Button>
          </div>
        </Show>
        <InfiniteList<CustomerType>
          items={mobileItems}
          keyField={c => c.id}
          hasMore={hasMore()}
          loading={mobileLoading()}
          onLoadMore={loadMore}
          onRefresh={async () => {
            await mobileFetch(true)
          }}
          emptyMessage={<p class="py-10 text-center text-foreground-subtle">Aún no has registrado clientes</p>}
          endMessage={<span>Has llegado al final de la lista de clientes</span>}
          class="space-y-3"
          renderItem={customer => (
            <SwipeAction
              itemId={customer.id}
              rememberPosition
              onClick={() => navigate(`/admin/clientes/${customer.id}` as Path)}
              leftOptions={[
                {
                  icon: 'material-symbols-light:visibility-outline',
                  label: 'Ver',
                  color: 'info',
                  action: () => navigate(`/admin/clientes/${customer.id}` as Path)
                },
                ...(can(P.customers.update)
                  ? [
                      {
                        icon: 'material-symbols-light:edit-outline',
                        label: 'Editar',
                        color: 'warning',
                        action: () => openEditModal(customer)
                      }
                    ]
                  : [])
              ]}
              rightOptions={[
                ...(can(P.customers.delete)
                  ? [
                      {
                        icon: 'material-symbols-light:delete-outline',
                        label: 'Eliminar',
                        color: 'danger',
                        action: () => handleDelete(customer.id)
                      }
                    ]
                  : [])
              ]}
            >
              <Card class="!rounded-lg">
                <Card.Body>
                  <div class="flex items-start gap-3">
                    <Show when={can(P.customers.delete)}>
                      <Checkbox size="sm" color="danger" checked={selectedIds().has(customer.id)} onChange={() => toggleSelect(customer.id)} class="mt-0.5 shrink-0" />
                    </Show>
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center justify-between gap-2">
                        <p class="font-medium text-foreground truncate">
                          {customer.tipoPersona === 'juridica' ? customer.razonSocial || customer.name : [customer.name, customer.lastName].filter(Boolean).join(' ')}
                        </p>
                        <div class="flex flex-col items-end gap-1 shrink-0">
                          <Badge color={estadoColor()[customer.estadoCliente]}>{estadoLabel()[customer.estadoCliente] ?? customer.estadoCliente}</Badge>
                          <Show when={customer.bloqueado}>
                            <Badge color="danger">Bloqueado</Badge>
                          </Show>
                        </div>
                      </div>
                      <div class="mt-2 space-y-1 text-sm">
                        <div class="flex items-center gap-2">
                          <Icon icon="material-symbols-light:badge-outline" size={16} class="text-foreground-subtle shrink-0" />
                          <span class="text-foreground truncate">
                            {customer.document || '—'} <span class="text-foreground-subtle">{customer.documentType || ''}</span>
                          </span>
                        </div>
                        <Show when={customer.phone || customer.celular || customer.email}>
                          <div class="flex items-center gap-2">
                            <Icon icon="material-symbols-light:call-outline" size={16} class="text-foreground-subtle shrink-0" />
                            <span class="text-foreground-subtle truncate">{customer.phone || customer.celular || customer.email}</span>
                          </div>
                        </Show>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </SwipeAction>
          )}
        />
      </Show>

      <TableFilters title="Filtros de clientes" isOpen={filtersOpen()} onClose={() => setFiltersOpen(false)} activeCount={activeFilters()} onClear={clearFilters}>
        <Input
          label="Buscar"
          value={filterBusqueda()}
          onInput={e => setFilterBusqueda(e.currentTarget.value)}
          placeholder="Nombre, documento, correo, teléfono..."
          startIcon="material-symbols-light:search"
        />

        <Select
          label="Estado"
          options={[{ value: '', title: 'Cualquier estado' }, ...Object.entries(estadoLabel()).map(([key, label]) => ({ value: key, title: label }))]}
          value={filterEstado()}
          onChange={v => setFilterEstado(String(v ?? ''))}
          class="w-full"
        />

        <Select
          label="Tipo de persona"
          options={[
            { value: '', title: 'Cualquiera' },
            { value: 'natural', title: 'Persona natural' },
            { value: 'juridica', title: 'Persona jurídica' }
          ]}
          value={filterTipo()}
          onChange={v => setFilterTipo(String(v ?? ''))}
          class="w-full"
        />

        <Input label="Ciudad" value={filterCiudad()} onInput={e => setFilterCiudad(e.currentTarget.value)} placeholder="Filtrar por ciudad..." />

        <div class="border-t border-outline/30 pt-4">
          <p class="text-sm font-medium text-foreground mb-3">Estado comercial</p>
          <Switch label="Sólo bloqueados" checked={filterBloqueado()} onChange={e => setFilterBloqueado(e.currentTarget.checked)} />
        </div>
      </TableFilters>

      <Modal isOpen={showModal()} onClose={() => setShowModal(false)} size="md">
        <Modal.Header>
          <h2 class="text-xl font-bold text-foreground">{editingCustomer() ? 'Editar Cliente' : 'Nuevo Cliente'}</h2>
          <p class="text-sm text-foreground-subtle">Podrás completar más datos desde la ficha del cliente</p>
        </Modal.Header>
        <Modal.Body>
          <Form id="customer-form" controller={form}>
            <div class="space-y-4">
              <div class="grid grid-cols-2 gap-4">
                <Input name="name" label="Nombre / razón social" />
                <Select name="documentType" label="Tipo de documento" options={documentOptions()} value={(form.values().documentType as string) ?? 'CC'} />
              </div>
              <div class="grid grid-cols-2 gap-4">
                <Input name="document" label="Número de documento" />
                <Input name="phone" label="Teléfono" />
              </div>
              <div class="grid grid-cols-2 gap-4">
                <Input name="celular" label="Celular" />
                <Input name="email" label="Correo" type="email" />
              </div>
              <Input name="address" label="Dirección" />
              <Input name="ciudad" label="Ciudad" />
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button type="button" variant="soft" color="default" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={form.submitting() || !form.valid()} form="customer-form">
              {form.submitting() ? 'Guardando...' : editingCustomer() ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* FAB móvil: en pantallas pequeñas las acciones del header no caben;
          el menú flotante ofrece las mismas opciones (filtros y crear). */}
      <div class="sm:hidden">
        <FabMenu
          items={[
            { label: 'Filtros', icon: 'material-symbols-light:tune', onClick: () => setFiltersOpen(true) },
            { label: 'Nuevo Cliente', icon: 'material-symbols-light:add-circle-outline', onClick: openCreateModal }
          ]}
        />
      </div>

      <alert.AlertComponent />
    </>
  )
}

export default CustomersPage
